import { MomentModalRef } from '@pages/Search/components/MomentGrid/components/MomentModal/MomentModal';
import { Media } from '@typings/Media';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export interface MomentModalState {
  isOpen: boolean;
  currentMedia: Media | null;
  isLoading: boolean;
  error: string | null;
}

export interface MomentModalActions {
  openModal: (media: Media) => void;
  closeModal: () => void;
  clearError: () => void;
}

export interface MomentModalContextValue extends MomentModalState, MomentModalActions {}

// More specific provider props
//
// No `onError`: reporting a modal crash is `SubtreeErrorBoundary`'s job (see `LibraryPage`),
// and the old callback duplicated it under a different Sentry tag, so one problem could
// produce two differently-grouped issues. The try/catch blocks below stay because they set
// the `error` state that `VideoCard` reads via `clearError`, and because
// `onModalStateChange` is a consumer callback that genuinely could throw.
interface MomentModalProviderProps {
  children: React.ReactNode;
  onModalStateChange?: (isOpen: boolean, media: Media | null) => void; // State change callback
}

const MomentModalContext = createContext<MomentModalContextValue | null>(null);

export const useMomentModal = (): MomentModalContextValue => {
  const context = useContext(MomentModalContext);
  if (!context) {
    throw new Error('useMomentModal must be used within a MomentModalProvider');
  }
  return context;
};

// Add a safe version that doesn't throw
export const useMomentModalSafe = (): MomentModalContextValue | null => {
  return useContext(MomentModalContext);
};

export const MomentModalProvider = ({ children, onModalStateChange }: MomentModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState<Media | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const momentModalRef = useRef<MomentModalRef>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const openModal = useCallback(
    (media: Media) => {
      try {
        setError(null);
        setIsLoading(true);
        setCurrentMedia(media);
        setIsOpen(true);
        onModalStateChange?.(true, media);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to open modal');
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [onModalStateChange],
  );

  const closeModal = useCallback(() => {
    try {
      setIsOpen(false);
      setCurrentMedia(null);
      setError(null);
      onModalStateChange?.(false, null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to close modal');
      setError(error.message);
    }
  }, [onModalStateChange]);

  const contextValue: MomentModalContextValue = useMemo(
    () => ({
      openModal,
      closeModal,
      clearError,
      isOpen,
      currentMedia,
      isLoading,
      error,
    }),
    [openModal, closeModal, clearError, isOpen, currentMedia, isLoading, error],
  );

  return <MomentModalContext.Provider value={contextValue}>{children}</MomentModalContext.Provider>;
};
